import * as React from "react";
import { styled, useTheme } from "@mui/material/styles";
import {
  Avatar,
  Box,
  Chip,
  Divider,
  Drawer,
  Button,
  Grid,
  List,
  ListItem,
  ListItemAvatar,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Rating,
  Stack,
  Typography,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import InboxIcon from "@mui/icons-material/MoveToInbox";
import MailIcon from "@mui/icons-material/Mail";
import StarIcon from "@mui/icons-material/Star";
import { useNavigate } from "react-router-dom";
import { Customer } from "../../types/customer";
import { GoogleMap, LoadScript } from "@react-google-maps/api";
import { GMAP_API_KEY } from "../../configs/api";
import MapPreview from "../../components/MapPreview";
import Linkify from "../../components/Linkify";
import { useUILabel } from "../../hooks/useUILabel";

// type Anchor = 'top' | 'left' | 'bottom' | 'right';

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(0, 2),
  marginTop: 40,
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
  justifyContent: "flex-end",
}));

const containerStyle = {
  width: "100%",
  height: "400px",
};

const options = {
  fullscreenControl: false, // remove the top-right button
  mapTypeControl: false, // remove the top-left buttons
  streetViewControl: false, // remove the pegman
  zoomControl: false, // remove the bottom-right buttons
  scrollwheel: false,
};

interface ClientDrawerProps {
  data: Customer;
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function ClientDrawer(props: ClientDrawerProps) {
  const { data, open, setOpen } = props;
  const navigate = useNavigate();
  const locationLabel = useUILabel("common.location");
  const phoneNumberLabel = useUILabel("common.phoneNumber");
  const emailLabel = useUILabel("common.email");
  const socialsLabel = useUILabel("common.socials");
  const moreDetailLabel = useUILabel("common.moreDetail");
  const linkedInColonLabel = useUILabel("client.linkedInColon");
  const facebookColonLabel = useUILabel("client.facebookColon");
  const instagramColonLabel = useUILabel("client.instagramColon");
  const websiteColonLabel = useUILabel("client.websiteColon");
  const nALabel = useUILabel("client.nA");
  const [lng, lat] = React.useMemo(
    () => data?.coordinate?.coordinates || [],
    [data]
  );

  const toggleDrawer =
    (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
      if (
        event.type === "keydown" &&
        ((event as React.KeyboardEvent).key === "Tab" ||
          (event as React.KeyboardEvent).key === "Shift")
      ) {
        return;
      }

      setOpen(open);
    };

  return (
    <div>
      <React.Fragment>
        {/*<Button onClick={toggleDrawer(anchor, true)}>{anchor}</Button>*/}
        <Drawer anchor="right" open={open} onClose={toggleDrawer(false)}>
          <Box sx={{ width: 450, mb: 8 }} role="presentation">
            <DrawerHeader>
              <ClearIcon
                onClick={toggleDrawer(false)}
                onKeyDown={toggleDrawer(false)}
              />
            </DrawerHeader>
            <Box
              sx={{
                borderRadius: 2,
                overflow: "hidden",
                width: "100%",
                mb: 2,
              }}
            >
            <MapPreview lat={lat} lng={lng} />
            </Box>
            <List>
              <ListItem alignItems="flex-start">
                <ListItemText
                  primary={`${data?.first_name || nALabel}`}
                  primaryTypographyProps={{ fontWeight: "500", fontSize: 18 }}
                  secondary={data?.city}
                  sx={{ flex: 1 }}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary={locationLabel}
                  primaryTypographyProps={{ fontWeight: "500" }}
                  secondary={`${data?.physical_address_1}, ${data?.city}, ${data?.zip}`}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary={phoneNumberLabel}
                  secondary={`${data?.phone_number}`}
                />
              </ListItem>
              <ListItem>
                <ListItemText primary={emailLabel} secondary={`${data?.email}`} />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary={socialsLabel}
                  primaryTypographyProps={{ fontWeight: "500" }}
                  secondary={
                    <Box sx={{ mt: 1 }}>
                      <Typography sx={{ fontSize: 14 }}>
                        {linkedInColonLabel} <Linkify url={data?.linked_in} />
                      </Typography>
                      <Typography sx={{ fontSize: 14 }}>
                        {facebookColonLabel} <Linkify url={data?.facebook} />
                      </Typography>
                      <Typography sx={{ fontSize: 14 }}>
                        {instagramColonLabel} <Linkify url={data?.instagram} />
                      </Typography>
                      <Typography sx={{ fontSize: 14 }}>
                        {websiteColonLabel} <Linkify url={data?.website} />
                      </Typography>
                    </Box>
                  }
                />
              </ListItem>
            </List>
            <Box display="flex" justifyContent="center" sx={{ mt: 2 }}>
              <Button
                variant="contained"
                onClick={() => navigate(`/clients/${data?.id}`)}
              >
                {moreDetailLabel}
              </Button>
            </Box>
          </Box>
        </Drawer>
      </React.Fragment>
    </div>
  );
}
